import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchRsvpDetails } from '../BackendRoute';
import { useLanguage } from '../LanguageContext';
import { useSnackbar } from '../SnackbarContext';
import HelloKittyBride from '../assets/images/hellokitty_bride.webp';
import HelloKittyGroom from '../assets/images/hellokitty_groom.webp';
import ReviewStep from '../rsvp-form/ReviewStep';
import { getTokenFromCookie } from '../utils/cookieUtils';

export default function RsvpPage() {
  const navigate = useNavigate();
  const [hasRsvp, setHasRsvp] = useState(false);
  const [rsvpDetails, setRsvpDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { t } = useLanguage();

  const getHasRsvp = async () => {
    // Try to use token from cookie, fallback to id from URL
    const token = getTokenFromCookie();
    if (!token) {
      showSnackbar(t('main.error_no_token'), 'error');
      return;
    }
    const details = await fetchRsvpDetails(token, showSnackbar);
    if (details) {
      setRsvpDetails(details);
      const isRsvpResponsePresent = Object.keys(details.responses).length > 0;
      setHasRsvp(isRsvpResponsePresent);
    }
  };

  useEffect(() => {
    getHasRsvp();
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  return (
    <div className="w-full flex-grow flex flex-col relative pt-4 pb-8 px-4">
      {/* For Desktop: Centered Unified Invitation Card */}
      <div className="hidden md:flex items-center justify-center w-full h-full min-h-[60vh]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="w-full max-w-md bg-white/90 backdrop-blur-md rounded-3xl border border-white/40 shadow-xl p-10 text-center flex flex-col justify-between space-y-8"
        >
          {/* Content Section */}
          <div className="space-y-4">
            <h1 className="text-6xl text-banner-title text-rose-accent">
              Daniel & Tracer
            </h1>

            {/* Date Information */}
            <div className="bg-wedding-blush/30 rounded-lg">
              <p className="text-lg uppercase tracking-[0.2em] text-panel-subtitle mb-1">
                {t('main.full_date')}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <img className="max-w-12 max-h-12" src={HelloKittyGroom} />
            <span className="mx-3 text-rose-accent/50 text-xs">✦</span>
            <img className="max-w-12 max-h-12" src={HelloKittyBride} />
          </div>

          {/* Action Button */}
          <div className="flex flex-col gap-4">
            {rsvpDetails?.is_locked ? (
              <>
                {hasRsvp && (
                  <motion.button
                    onClick={() => setIsModalOpen(true)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="w-full py-4 px-6 btn-wedding-inverted font-raleway font-semibold text-xs md:text-sm uppercase tracking-[0.25em] rounded-full shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    {t('main.view_rsvp_btn')}
                  </motion.button>
                )}
                <p className="text-base text-panel-subtitle italic my-2">
                  {t('main.locked_message')}
                </p>
              </>
            ) : (
              <>
                <motion.button
                  onClick={() => navigate('/rsvp')}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full py-4 px-6 bg-rose-accent hover:bg-rose-hover text-white font-raleway font-semibold text-xs md:text-sm uppercase tracking-[0.25em] rounded-full shadow-md hover:shadow-lg transition-all duration-300"
                >
                  {hasRsvp ? t('main.edit_rsvp_btn') : t('main.rsvp_btn')}
                </motion.button>

                {hasRsvp && (
                  <motion.button
                    onClick={() => setIsModalOpen(true)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="w-full py-4 px-6 btn-wedding-inverted font-raleway font-semibold text-xs md:text-sm uppercase tracking-[0.25em] rounded-full shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    {t('main.view_rsvp_btn')}
                  </motion.button>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="w-full max-w-md bg-white/95 rounded-3xl border border-white/40 shadow-2xl p-6 md:p-8 overflow-y-auto max-h-[85vh] relative no-scrollbar"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-nav-comp-text hover:text-rose-accent transition-colors duration-300 w-8 h-8 rounded-full flex items-center justify-center bg-wedding-blush/20 hover:bg-wedding-blush/50 font-sans"
                aria-label="Close"
              >
                ✕
              </button>

              {/* ReviewStep Content */}
              <ReviewStep
                titleKey="rsvp_form.view_review"
                formData={rsvpDetails?.responses || {}}
                allGuests={rsvpDetails?.all_guests || []}
              />

              {/* Action Button to close */}
              <div className="mt-8">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="w-full py-3 px-4 bg-rose-accent hover:bg-rose-hover text-white font-raleway font-semibold text-xs uppercase tracking-[0.2em] rounded-full shadow-sm transition-all duration-300"
                >
                  {t('rsvp_form.back') || 'Close'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
